import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, Inbox, UserCircle, ArrowRight, Layers,
  Wifi, WifiOff, Circle, Settings, PlayCircle, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CsmWorkspacePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [agents, setAgents] = useState([]);
  const [poolCount, setPoolCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/csm/workspace`, { headers: authHeader() });
        setData(r.data);
      } catch (err) { /* silent */ }
      finally { setLoading(false); }
    })();
    loadAwaState();
    // Heartbeat every 60s so the agent stays "online"
    const beat = setInterval(() => {
      axios.post(`${API_URL}/api/csm/agents/me/heartbeat`, {}, { headers: authHeader() })
        .then(() => loadAwaState()).catch(() => {});
    }, 60000);
    return () => clearInterval(beat);
  }, []);

  const loadAwaState = async () => {
    try {
      const [meRes, agentsRes, poolRes] = await Promise.all([
        axios.get(`${API_URL}/api/csm/agents/me`, { headers: authHeader() }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/csm/agents`, { headers: authHeader() }).catch(() => ({ data: { agents: [] } })),
        axios.get(`${API_URL}/api/csm/work-pool`, { headers: authHeader() }).catch(() => ({ data: { tickets: [] } })),
      ]);
      setMe(meRes.data);
      setAgents(agentsRes.data?.agents || []);
      setPoolCount(poolRes.data?.tickets?.length || 0);
    } catch (err) { /* silent */ }
  };

  const setStatus = async (status) => {
    try {
      await axios.put(`${API_URL}/api/csm/agents/me/status`, { status }, { headers: authHeader() });
      toast.success(`You're now ${status}`);
      loadAwaState();
    } catch (err) { toast.error('Status update failed'); }
  };

  const runAwa = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/csm/awa/run`, {}, { headers: authHeader() });
      toast.success(`AWA sweep: ${r.data.assigned} assigned, ${r.data.pooled} pooled`);
      loadAwaState();
    } catch (err) { toast.error(err.response?.data?.detail || 'AWA sweep failed'); }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!data) return <div className="p-12 text-center text-[#64748B]">Could not load workspace</div>;

  return (
    <div className="p-6 space-y-6" data-testid="csm-workspace">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Support Workspace</h1>
          <p className="text-sm text-[#64748B] mt-1">Queue overview with Advanced Work Assignment.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/csm/awa" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
            <Settings className="w-3.5 h-3.5" /> Rules &amp; agents
          </Link>
          <Link to="/admin/csm/config" className="text-sm text-[#0E7490] hover:underline">Queues + SLAs →</Link>
        </div>
      </div>

      {/* AWA presence + actions strip */}
      {me && (
        <Card className="p-4 border-[#E2E8F0]" data-testid="awa-presence-strip">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <StatusDot status={me.status} />
              <div>
                <div className="text-sm font-semibold text-[#0A2342]">
                  You're {me.status}
                  {me.manual_status && <span className="text-[10px] text-[#94A3B8] ml-1">(manual)</span>}
                </div>
                <div className="text-[11px] text-[#64748B]">
                  Workload {me.current_workload}/{me.max_concurrent_tickets}
                  {poolCount > 0 && (
                    <> · <Link to="/admin/csm/awa" className="text-[#0E7490] hover:underline">{poolCount} ticket{poolCount === 1 ? '' : 's'} in pool</Link></>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant={me.manual_status === 'online' ? 'default' : 'outline'}
                      onClick={() => setStatus('online')} className={me.manual_status === 'online' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                      data-testid="set-online-btn">
                <Wifi className="w-3.5 h-3.5 mr-1" /> Online
              </Button>
              <Button size="sm" variant={me.manual_status === 'away' ? 'default' : 'outline'}
                      onClick={() => setStatus('away')} className={me.manual_status === 'away' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                      data-testid="set-away-btn">
                Away
              </Button>
              <Button size="sm" variant={me.manual_status === 'offline' ? 'default' : 'outline'}
                      onClick={() => setStatus('offline')} className={me.manual_status === 'offline' ? 'bg-slate-500 hover:bg-slate-600 text-white' : ''}
                      data-testid="set-offline-btn">
                <WifiOff className="w-3.5 h-3.5 mr-1" /> Offline
              </Button>
              <Button size="sm" variant="outline" onClick={runAwa} data-testid="run-awa-btn">
                <PlayCircle className="w-3.5 h-3.5 mr-1" /> Run AWA
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Agent roster */}
      {agents.length > 0 && (
        <Card className="p-4 border-[#E2E8F0]" data-testid="agent-roster">
          <h3 className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0E7490]" /> Agents on shift
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">
              {agents.filter(a => a.status === 'online').length} online · {agents.length} total
            </Badge>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {agents.map(a => (
              <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded border border-[#F1F5F9]" data-testid={`agent-row-${a.user_id}`}>
                <StatusDot status={a.status} small />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#0A2342] truncate">{a.name}</div>
                  <div className="text-[10px] text-[#64748B]">{a.current_workload}/{a.max_concurrent_tickets}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi
          label="SLA Breached"
          value={data.breached}
          icon={AlertTriangle}
          color="rose"
          onClick={() => navigate('/admin/csm/tickets?breached=true')}
          testid="kpi-breached"
        />
        <Kpi
          label="Unassigned"
          value={data.unassigned}
          icon={Inbox}
          color="amber"
          onClick={() => navigate('/admin/csm/tickets?assigned_agent_id=unassigned')}
          testid="kpi-unassigned"
        />
        <Kpi
          label="My Open Tickets"
          value={data.my_open}
          icon={UserCircle}
          color="teal"
          onClick={() => navigate('/admin/csm/tickets?assigned_agent_id=me')}
          testid="kpi-mine"
        />
      </div>

      {/* Queues */}
      <div>
        <h2 className="text-base font-semibold text-[#0A2342] mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#0E7490]" /> Queues
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.queues.map(q => {
            const c = data.counts[q.id] || {};
            return (
              <Card key={q.id} className="p-4 border-[#E2E8F0] hover:border-[#0E7490] transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/csm/tickets?queue_id=${q.id}`)}
                    data-testid={`queue-${q.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-[#0A2342]">{q.name}</h3>
                    {q.description && <p className="text-xs text-[#64748B] mt-1">{q.description}</p>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs">
                  <Badge className="bg-[#0E7490]/10 text-[#0E7490] border-[#0E7490]/20">{c.open || 0} active</Badge>
                  {c.pending > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200">{c.pending} pending customer</Badge>}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, onClick, testid }) {
  const colors = {
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    teal: 'bg-[#F0FDFA] text-[#0E7490] border-[#0E7490]/30',
  };
  return (
    <Card className={`p-5 border cursor-pointer hover:shadow-md transition-shadow ${colors[color]}`}
          onClick={onClick} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-70" />
      </div>
    </Card>
  );
}

function StatusDot({ status, small }) {
  const color = status === 'online' ? '#10B981' : status === 'away' ? '#F59E0B' : '#94A3B8';
  const size = small ? 'w-2 h-2' : 'w-3 h-3';
  return <Circle className={`${size} fill-current`} style={{ color }} />;
}
